import priceFormat from "../../utils/priceFormat";

/**
 * Formatters for a buyer's wants & needs.
 *
 * These read differently from the listing formatters on purpose. A listing with
 * no square footage is missing data and the field is hidden; a buyer with no
 * square-footage requirement is telling you something — "Any" — so the row keeps
 * the label. Only genuinely absent text, like the free-form note, disappears.
 */

const range = (min, max, format) => {
  if (min && max) return `${format(min)} - ${format(max)}`;
  if (min) return `${format(min)}+`;
  if (max) return `Up to ${format(max)}`;
  return "Any";
};

const money = (n) => `$${priceFormat(n) ?? n}`;
const plain = (n) => Number(n).toLocaleString();

export const formatNeedPrice = (criteria) =>
  range(criteria?.minPrice, criteria?.maxPrice, money);

export const formatNeedSqFt = (criteria) =>
  range(criteria?.minSqFt, criteria?.maxSqFt, plain);

/** "3+" when only a minimum is set, "3 - 4" when the buyer gave a range. */
export const formatNeedCount = (min, max) => {
  if (min && max && max !== min) return `${min} - ${max}`;
  if (min) return `${min}+`;
  if (max) return `Up to ${max}`;
  return "Any";
};

/** Cities if the buyer named any, otherwise the zip codes, otherwise "Any". */
export const formatNeedLocation = (criteria) => {
  const cities = criteria?.cities?.filter(Boolean) ?? [];
  if (cities.length) return cities.join(", ");

  const zips = criteria?.zipCodes?.filter(Boolean) ?? [];
  if (zips.length) return zips.join(", ");

  return "Any";
};

/**
 * The last four characters of the buyer's id, which is how a buyer is referred
 * to out loud in a meeting. Never their name.
 */
export const formatBuyerRef = (criteria) => {
  const id = criteria?.buyerId || criteria?._id;
  return id ? String(id).slice(-4).toUpperCase() : null;
};

/**
 * A price range short enough to sit on one line of a match row: "$300k – $450k".
 * The full figures stay on the Buyer Needs tab, where there is room for them.
 */
export const formatNeedPriceCompact = (criteria) => {
  const round = (n) =>
    n >= 1000000
      ? `$${(n / 1000000).toFixed(n % 1000000 ? 1 : 0)}m`
      : `$${Math.round(n / 1000)}k`;

  const { minPrice: min, maxPrice: max } = criteria ?? {};
  if (min && max) return `${round(min)} – ${round(max)}`;
  if (min) return `${round(min)}+`;
  if (max) return `Up to ${round(max)}`;
  return "Any price";
};

/** "$300k – $450k · 3+ bd · Abilene" — one buyer's ask, in a single line. */
export const formatMatchSummary = (criteria) =>
  [
    formatNeedPriceCompact(criteria),
    criteria?.minBeds ? `${criteria.minBeds}+ bd` : null,
    formatNeedLocation(criteria),
  ]
    .filter(Boolean)
    .join(" · ");
